import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getStateForCity } from '../lib/cityStateMap';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';
import SecurityWatermark from './SecurityWatermark';
import { isRestricted, isUnrestricted, maskPhone, maskEmail, applyRestrictedSearch, MIN_SEARCH_CHARS } from '../lib/restrictions';

const AgentManagement = ({ user }) => {
  const restricted = isRestricted(user);
  const [agents, setAgents] = useState([]);
  const bulk = useBulkSelect();
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCities, setFilterCities] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterAreas, setFilterAreas] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', area: '', city: '', state: '', commission_percentage: 0 });
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const navigate = useNavigate();

  const handleDownload = async (format) => {
    try {
      const res = await api.get(`/downloads/agents/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `agents.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try { const res = await api.get('/agents'); setAgents(res.data); }
    catch { toast.error('Failed to load agents'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: name === 'commission_percentage' ? parseFloat(value) || 0 : value };
      if (name === 'city') {
        const autoState = getStateForCity(value);
        if (autoState && !prev.state) next.state = autoState;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgent) {
        await api.put(`/agents/${editingAgent.id}`, form);
        toast.success('Agent updated!');
      } else {
        await api.post('/agents', form);
        toast.success('Agent created!');
      }
      setIsDialogOpen(false); resetForm(); fetchAgents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save agent'); }
  };

  const startEdit = (agent) => {
    setEditingAgent(agent);
    setForm({ name: agent.name, phone: agent.phone, email: agent.email || '', area: agent.area || '', city: agent.city || '', state: agent.state || '', commission_percentage: agent.commission_percentage || 0 });
    setIsDialogOpen(true);
  };

  const deleteAgent = async (id) => {
    if (!window.confirm('Delete this agent?')) return;
    try { await api.delete(`/agents/${id}`); toast.success('Agent deleted'); fetchAgents(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const resetForm = () => { setForm({ name: '', phone: '', email: '', area: '', city: '', state: '', commission_percentage: 0 }); setEditingAgent(null); };
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`; };

  const cityOptions = [...new Set(agents.filter(a => !filterStates.length || filterStates.includes(a.state)).map(a => a.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const stateOptions = [...new Set(agents.map(a => a.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const areaOptions = [...new Set(agents.map(a => a.area).filter(Boolean))].sort().map(a => ({ value: a, label: a }));
  const hasActiveFilters = !!(searchTerm || filterCities.length || filterStates.length || filterAreas.length);

  const filtered = restricted
    ? applyRestrictedSearch(user, agents, searchTerm)
    : agents.filter(a => {
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          if (!a.name?.toLowerCase().includes(q) && !a.phone?.includes(searchTerm) && !a.email?.toLowerCase().includes(q) && !(a.city || '').toLowerCase().includes(q) && !(a.state || '').toLowerCase().includes(q) && !(a.area || '').toLowerCase().includes(q)) return false;
        }
        if (filterCities.length && !filterCities.includes(a.city)) return false;
        if (filterStates.length && !filterStates.includes(a.state)) return false;
        if (filterAreas.length && !filterAreas.includes(a.area)) return false;
        return true;
      }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="agent-management-container">
      {restricted && <SecurityWatermark user={user} label="AGENTS • CONFIDENTIAL" />}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Agents</h1>
          <p className="text-gray-600 mt-1">Manage your sales agents network</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50 gap-1.5" onClick={() => handleDownload('excel')} data-testid="download-agents-excel">
                    <th className="p-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filtered.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Excel
              </Button>
              <Button variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50 gap-1.5" onClick={() => handleDownload('pdf')} data-testid="download-agents-pdf">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF
              </Button>
            </>
          )}
        {canManage && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-lg z-50">
              <DialogHeader>
                <DialogTitle>{editingAgent ? 'Edit Agent' : 'Add New Agent'}</DialogTitle>
                <DialogDescription>Sales agent details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Name *</Label><Input name="name" value={form.name} onChange={handleChange} required data-testid="agent-name-input" /></div>
                  <div className="space-y-2"><Label>Phone *</Label><Input name="phone" value={form.phone} onChange={handleChange} required data-testid="agent-phone-input" /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" value={form.email} onChange={handleChange} data-testid="agent-email-input" /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Area</Label><Input name="area" value={form.area} onChange={handleChange} /></div>
                  <div className="space-y-2"><Label>City</Label><Input name="city" value={form.city} onChange={handleChange} /></div>
                  <div className="space-y-2"><Label>State</Label><Input name="state" value={form.state} onChange={handleChange} /></div>
                </div>
                <div className="space-y-2"><Label>Commission %</Label><Input name="commission_percentage" type="number" step="0.1" min="0" max="100" value={form.commission_percentage} onChange={handleChange} data-testid="agent-commission-input" /></div>
                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1" data-testid="submit-agent-button">{editingAgent ? 'Update Agent' : 'Create Agent'}</Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card data-testid="agent-filters">
        <CardContent className="p-4 space-y-3">
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name, phone, or email..." className="max-w-md" data-testid="agent-search-input" />
          <div className="flex flex-wrap gap-2 items-center">
            <MultiSelectFilter label="City" options={cityOptions} selected={filterCities} onChange={setFilterCities} testId="agent-filter-city" />
            <MultiSelectFilter label="State" options={stateOptions} selected={filterStates} onChange={setFilterStates} testId="agent-filter-state" />
            <MultiSelectFilter label="Area" options={areaOptions} selected={filterAreas} onChange={setFilterAreas} testId="agent-filter-area" />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => { setSearchTerm(''); setFilterCities([]); setFilterStates([]); setFilterAreas([]); }} data-testid="agent-clear-filters">
                Clear All
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500">Showing {filtered.length} of {agents.length} agents</p>}
        </CardContent>
      </Card>

      <Card data-testid="agents-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Agents ({filtered.length})</CardTitle>
            {canManage && (
              <Button size="sm" className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-sm" onClick={() => setIsDialogOpen(true)} data-testid="add-agent-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add Agent
              </Button>
            )}
          </div>
          <CardDescription>Your sales agent network</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="agents-empty">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              {restricted ? (
                (searchTerm || '').trim().length < MIN_SEARCH_CHARS ? (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Required</h3>
                    <p className="text-gray-600">Type at least {MIN_SEARCH_CHARS} characters to look up an agent.</p>
                    <p className="text-xs text-gray-400 mt-2">For your role, the full agent list is not displayed. Up to 10 search results are shown.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Match</h3>
                    <p className="text-gray-600">No agent matches "{searchTerm}". Try a more specific name or phone.</p>
                  </>
                )
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{searchTerm ? 'No Agents Found' : 'No Agents Yet'}</h3>
                  <p className="text-gray-600">{searchTerm ? 'Try a different search term.' : 'Add your first sales agent.'}</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="agents-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left font-semibold text-gray-600">Name</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Phone</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Location</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Commission</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Added</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(agent => (
                    <tr key={agent.id} className={`border-t hover:bg-gray-50 ${bulk.isSelected(agent.id) ? "bg-blue-50" : ""}`} data-testid={`agent-row-${agent.id}`}>
                    {!restricted && <td className="p-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(agent.id)} onChange={() => bulk.toggleSelect(agent.id)} /></td>}
                      <td className="p-3 font-semibold text-gray-900">{agent.name}</td>
                      <td className="p-3 text-gray-700">{restricted ? maskPhone(agent.phone) : agent.phone}</td>
                      <td className="p-3 text-gray-500 text-xs">{restricted ? (agent.email ? maskEmail(agent.email) : '-') : (agent.email || '-')}</td>
                      <td className="p-3 text-gray-500 text-xs">{[agent.area, agent.city, agent.state].filter(Boolean).join(', ') || '-'}</td>
                      <td className="p-3 text-center"><Badge className="bg-violet-100 text-violet-700">{agent.commission_percentage || 0}%</Badge></td>
                      <td className="p-3 text-xs text-gray-400">{fmtDate(agent.created_at)}</td>
                      <td className="p-3 text-center">
                        {canManage && (
                          <div className="flex justify-center gap-1">
                            <Button size="sm" variant="ghost" className="text-blue-500 hover:text-blue-700 h-7 px-2" onClick={() => startEdit(agent)} data-testid={`edit-agent-${agent.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </Button>
                            <Button size="sm" variant="ghost" className="text-indigo-600 hover:bg-indigo-50 h-7 px-2" onClick={() => navigate(`/agents/${agent.id}/sales`)} data-testid={`view-sales-agent-${agent.id}`} title="View Sales by Party">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3v18h18M7 14l4-4 4 4 5-5" /></svg>
                            </Button>
                            <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" onClick={() => navigate(`/ledger-report?type=agent&id=${agent.id}`)} data-testid={`ledger-agent-${agent.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 px-2" onClick={() => deleteAgent(agent.id)} data-testid={`delete-agent-${agent.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="AGENT" label="agents" onComplete={() => { bulk.clearSelection(); fetchAgents(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default AgentManagement;
